import { ENDPOINTS } from 'config/endpoints';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { ResponsePaginated } from 'crono-fe-common/types/response';
import { ProspectSearch } from 'crono-fe-common/types/DTO/prospectSearch';
import { Prospect } from 'crono-fe-common/types/prospect';

export default function useSearchProspects<
  TData = ResponsePaginated<Prospect[]>,
>(
  searchParameters: ProspectSearch,
  enabled = true,
  select?: (data: ResponsePaginated<Prospect[]>) => TData,
) {
  const { selected, ...rest } = searchParameters;
  const request: Request = {
    url: `${ENDPOINTS.prospect.search.main}`,
    config: {
      method: 'post',
      data: { data: rest },
    },
  };

  return useQuery<ResponsePaginated<Prospect[]>, CronoError, TData>({
    queryKey: [ENDPOINTS.prospect.search.main, searchParameters],
    queryFn: () => client(request),
    staleTime: 5 * 60 * 1000, // 5 minutes
    keepPreviousData: true,
    enabled,
    select,
  });
}

export function useSearchProspectsInfiniteScroll(
  searchParameters: Omit<ProspectSearch, 'offset'>,
  keepPreviousData = true,
) {
  const { selected, ...rest } = searchParameters;
  const requestConfig = {
    queryKey: [ENDPOINTS.prospect.search.main, searchParameters],
    queryFn: ({ pageParam = 0 }) => {
      const request: Request = {
        url: ENDPOINTS.prospect.search.main,
        config: {
          method: 'post',
          data: { data: { ...rest, offset: pageParam } },
        },
      };
      return client(request);
    },
    getNextPageParam: (
      lastPage: ResponsePaginated<Prospect[]>,
      pages: ResponsePaginated<Prospect[]>[],
    ) => {
      const howManyLoaded = pages.reduce(
        (sum, page) => sum + page.data.data.length,
        0,
      );

      const checkIfHasNextPage =
        lastPage.data.data.length === 0 ||
        lastPage.data.data.length < searchParameters.limit;

      if (checkIfHasNextPage) {
        return undefined;
      }

      return howManyLoaded;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData,
  };

  return useInfiniteQuery<ResponsePaginated<Prospect[]>, CronoError>(
    requestConfig,
  );
}
